<template>
    <div>
        <create-customer-form
            v-model="customerModalSelected"
            @success="customerCreated"
        ></create-customer-form>
        <b-form @submit.stop.prevent="submitOrder" ref="customerForm">
            <b-form-row>
                <b-col cols="3">
                    <label class="font-weight-bold">Customer</label>
                </b-col>

                <b-col>
                    <customer-type-ahead
                        v-model="orderModel.customer_id"
                        @set-customer="customer = $event"
                        required
                        :disabled="!isNew"
                    ></customer-type-ahead>
                </b-col>
                <b-col cols="1" v-if="isNew">
                    <b-button @click="customerModalSelected = true" block>
                        <b-icon icon="plus-circle"></b-icon
                    ></b-button>
                </b-col>
            </b-form-row>
            <br />
            <b-form-group
                id="input-group-type"
                label="Type:"
                label-for="input-type"
                label-class="font-weight-bold pt-0"
                label-cols="3"
            >
                <b-form-select
                    id="input-type"
                    v-model="orderModel.order_type_id"
                    :options="orderTypes | mapOptions"
                    :disabled="!isNew"
                    required
                >
                    <template #first>
                        <b-form-select-option :value="null" disabled
                            >Select Order Type</b-form-select-option
                        >
                    </template>
                </b-form-select>
            </b-form-group>

            <b-form-group
                id="input-group-description"
                label="Description:"
                label-for="input-description"
                label-class="font-weight-bold pt-0"
                label-cols="3"
                class="mt-6"
            >
                <b-form-textarea
                    id="input-description"
                    v-model="orderModel.description"
                    placeholder="Brief description of order..."
                    rows="3"
                    max-rows="4"
                    :disabled="!editMode"
                ></b-form-textarea>
            </b-form-group>

            <b-form-group
                id="input-group-driver"
                label="Assigned To:"
                label-for="input-driver"
                label-class="font-weight-bold pt-0"
                label-cols="3"
            >
                <b-form-select
                    id="input-driver"
                    v-model="orderModel.driver_id"
                    :options="drivers | mapOptions(true)"
                    required
                    :disabled="!editMode"
                >
                    <template #first>
                        <b-form-select-option :value="null" disabled
                            >Select Officer</b-form-select-option
                        >
                    </template>
                </b-form-select>
            </b-form-group>

            <b-form-group
                id="input-group-date"
                label="Due Date:"
                label-for="input-date"
                label-class="font-weight-bold pt-0"
                label-cols="3"
            >
                <b-form-input
                    id="input-date"
                    v-model="orderModel.date"
                    type="date"
                    required
                    :disabled="!editMode"
                ></b-form-input>
            </b-form-group>

            <b-form-group
                id="input-group-time"
                label="Due Time:"
                label-for="input-time"
                label-class="font-weight-bold pt-0"
                label-cols="3"
            >
                <b-form-input
                    id="input-time"
                    v-model="orderModel.time"
                    type="time"
                    required
                    :disabled="!editMode"
                ></b-form-input>
            </b-form-group>

            <!--   <b-form-checkbox
                        id="checkbox-signature"
                        v-model="orderModel.signature"
                        name="checkbox-signature"
                        :value="true"
                        :unchecked-value="false"
                    >
                        Require Signature
                    </b-form-checkbox> -->
            <div v-if="isNew">
                <b-form-row>
                    <b-col offset="3" cols="2">
                        <b-form-checkbox
                            id="checkbox-template"
                            v-model="isTemplate"
                            name="checkbox-template"
                            :value="true"
                            :unchecked-value="false"
                        >
                            Use Template?
                        </b-form-checkbox>
                    </b-col>
                </b-form-row>

                <select-order-template
                    v-if="isTemplate"
                    v-model="orderModel.order_template_id"
                    :order-type-id="orderModel.order_type_id"
                ></select-order-template>

                <b-card v-if="isTemplate && orderModel.customer_id">
                    <view-assets
                        v-model="orderModel.assets"
                        :customer-id="orderModel.customer_id"
                    />
                </b-card>
            </div>

            <div class="text-right" v-if="isNew">
                <b-button variant="warning" to="/admin/orders">Cancel</b-button>
                <b-button
                    type="submit"
                    class="my-4"
                    variant="primary"
                    :disabled="submitting ? true : false"
                    >{{ submitting ? 'Creating' : 'Create' }}</b-button
                >
            </div>
            <div
                class="text-right"
                v-if="!isNew && user_permissions.includes('update.orders')"
            >
                <b-button variant="warning" to="/admin/orders">Cancel</b-button>
                <b-button
                    variant="primary"
                    @click="$emit('save-order', orderModel)"
                    :disabled="updating || viewMode"
                    >{{
                        updating ? 'Saving Changes...' : 'Save Changes'
                    }}</b-button
                >
                <b-button
                    variant="secondary"
                    @click="$emit('change-mode', !viewMode)"
                    >{{ viewMode ? 'Edit Order' : 'Cancel Edit' }}</b-button
                >
                <b-button
                    variant="danger"
                    @click="$bvModal.show('order-template-modal')"
                    >Create Template</b-button
                >
            </div>
        </b-form>
    </div>
</template>
<script>
import order_api from '@/api/order'
import CreateCustomerForm from '@/views/Customers/CreateCustomerForm'

import orderHelperMixins from '@/mixins/orderHelperMixins'
import popupMixins from '@/mixins/popupMixins'
import CustomerTypeAhead from '@/components/CustomerTypeAhead.vue'

import SelectOrderTemplate from '@/components/SelectOrderTemplate'
import ViewAssets from '@/components/ViewAssets'

const scheduledDate = new Date(Date.now() + 24 * 60 * 60 * 1000)
    .toISOString()
    .split('T')[0]
const scheduledTime = '09:30:00'

const orderModel = {
    order_type_id: null,
    customer_id: null,
    description: null,
    driver_id: null,
    order_template_id: null,
    date: scheduledDate,
    time: scheduledTime,
    signature: false,
    assets: [],
    delivery_location_section_id: null,
}

const model_keys = [
    'id',
    'completed',
    'order_type_id',
    'customer_id',
    'description',
    'driver_id',
    'order_template_id',
    'schedule_at',
    'date',
    'time',
    'signature',
    'assets',
    'delivery_location_section_id',
]

export default {
    name: 'view-order-summary',
    components: {
        CreateCustomerForm,
        CustomerTypeAhead,
        SelectOrderTemplate,
        ViewAssets,
    },
    data() {
        return {
            customer: {},
            isTemplate: false,
            customerModalSelected: false,
            orderModel: { ...orderModel },
            category_id: null,
            submitting: false,
            hasErrors: false,
            stableState: false,
            previousCreatedOrderData: {},
        }
    },
    mixins: [orderHelperMixins, popupMixins],
    props: ['propData', 'viewMode', 'updating', 'isNew'],
    filters: {
        capitalize: function (value) {
            if (!value) return ''
            value = value.toString()
            return value.charAt(0).toUpperCase() + value.slice(1)
        },
        mapOptions(arr, isUser) {
            return arr.map(x => ({
                value: x.id,
                text: isUser ? `${x.first_name} ${x.last_name}` : x.name,
            }))
        },
    },
    computed: {
        editMode() {
            return this.isNew ? true : !this.viewMode
        },
        signature_options() {
            return [
                {
                    value: true,
                    text: 'Yes',
                },
                {
                    value: false,
                    text: 'No',
                },
            ]
        },
        user_permissions() {
            return this.$store.getters.user_permissions
        },
        order() {
            return this.$store.state.orders.item
        },
        drivers() {
            return this.$store.state.orders.drivers
        },

        customerName() {
            return this.order && this.order.customer && this.order.customer.name
        },

        orderTypes() {
            const orderTypes = this.$store.state.orderType.items
            console.log('orderTypes', orderTypes)
            return orderTypes
        },
        orderType() {
            return (
                this.orderTypes.find(x => x.id === this.order.order_type_id) ||
                {}
            )
        },
    },
    watch: {
        propData: {
            handler: function (val, from) {
                console.log('propData-from', from)
                console.log('propData', val)
                this.setFormData(val, false, 'watch proData')
            },
            deep: true,
        },

        $route(to) {
            //route changes
            this.stableState = false
            console.log('route to id', to.params.id)
            const isNew = parseInt(to.params.id, 10) == 0
            this.setFormData(
                { ...this.previousCreatedOrderData },
                isNew,
                'routed'
            )
        },

        'orderModel.reference_no': function () {
            this.syncOrder()
        },
        'orderModel.driver_id': function () {
            this.syncOrder()
        },
        'orderModel.description': function () {
            this.syncOrder()
        },
        'orderModel.date': function () {
            this.syncOrder()
        },
        'orderModel.time': function () {
            this.syncOrder()
        },

        'orderModel.customer_id': function (id) {
            this.getLastCustomerOrder(id)
            this.syncOrder()
        },
    },
    mounted() {
        this.setFormData(this.propData, false, 'mounted')
    },
    methods: {
        async getLastCustomerOrder(customer_id) {
            const query = ''
            const page = 1
            const filters = { customer_id }
            const result = await order_api.fetch(query, filters, page)
            let orders = result.data.success.data.data
            console.log('first order', orders[0])
            if (orders.length > 0) {
                this.orderModel.driver_id = orders[0].driver_id
            }
        },
        customerCreated(customer) {
            console.log('customer evt', customer)
            this.orderModel.customer_id = customer.id
            this.customerModalSelected = false
        },

        syncOrder() {
            const $this = this
            if (!$this.isNew) {
                $this.$nextTick(() => {
                    $this.$emit('sync-order', this.orderModel)
                })
            }
        },

        setFormData(val, isNew, type) {
            if (this.stableState) return
            const cleanValue = value =>
                typeof value == 'undefined' ? null : value

            isNew = isNew || !!this.isNew

            let data = { ...(isNew ? orderModel : val) }

            console.log('setFormData type', type)
            console.log('setFormData val', val)
            console.log('setFormData isNew', isNew)
            console.log('setFormData data', data)

            //empty model first
            for (let key in this.orderModel) {
                delete this.orderModel[key]
            }

            //set model reactively
            for (let key of model_keys) {
                switch (key) {
                    case 'date':
                    case 'time':
                    case 'schedule_at': {
                        if (this.orderModel[key]) break
                        const { date, time } = this.getScheduleAtParts(
                            data.schedule_at
                        )
                        this.$set(this.orderModel, 'date', data.date || date)
                        this.$set(this.orderModel, 'time', data.time || time)
                        break
                    }
                    case 'completed': {
                        this.$set(this.orderModel, key, !!data[key])
                        break
                    }
                    default:
                        this.$set(this.orderModel, key, cleanValue(data[key]))
                }
            }
            console.log('setformData', this.orderModel)
            this.stableState = isNew || !!val.id
        },
        async createTemplate() {
            try {
                const template = {
                    order_category_id:
                        this.orderModel.newTemplate.order_category_id,
                    name: this.orderModel.newTemplate.order_template_name,
                    description: this.orderModel.description,
                }
                const { id } = this.order
                const data = await this.$store.dispatch(
                    'orders/createTemplate',
                    {
                        id,
                        template,
                    }
                )
                console.log('results', data)
            } catch (err) {
                const responseMessage = err.response.data.error.message
                let errorMessage = 'Unable to create order template. '
                console.log(err.response.data.error)

                // Check if the fields have not been set
                if (responseMessage.includes('cannot be null')) {
                    errorMessage +=
                        'Please set a name and select an order category for your template'

                    // Checks if the order template already exists
                } else if (responseMessage.includes('Duplicate entry')) {
                    errorMessage +=
                        'Name of Order template already exists. Please select a new name'
                } else {
                    errorMessage += responseMessage
                }

                this.doToast('Error', errorMessage, 'danger')
            }
        },

        async submitOrder() {
            try {
                this.submitting = true
                const model = { ...this.orderModel }

                if (!model.customer_id)
                    throw new Error('Customer Must Be selected')
                if (!model.order_type_id)
                    throw new Error('Order Type Must Be selected')
                if (!model.driver_id)
                    throw new Error('Officer Must Be selected')
                model.schedule_at = this.getScheduleAt(model.date, model.time)
                model.delivery_location_section_id =
                    this.customer && this.customer.location_section_id
                console.log('model', model)
                const valid = await this.$validator.validate()
                if (!valid) {
                    throw new Error('Invalid Entry!!')
                }

                model.signature = model.signature || false

                const data = await this.$store.dispatch('orders/create', model)
                for (let key in data) {
                    this.$set(this.previousCreatedOrderData, key, data[key])
                }
                console.log('created data', data)
                this.doToast(
                    'Success',
                    `Order ${data.reference_no} Created!`,
                    'success'
                )

                this.$router.push(`/admin/orders/${data.id}`)

                /*   await this.$bvModal.msgBoxOk(
                    `The Reference No is ${data.reference_no}`,
                    {
                        title: 'Order Created Successfully',
                        size: 'sm',
                        buttonSize: 'sm',
                        okVariant: 'success',
                        headerClass: 'p-2 border-bottom-0',
                        footerClass: 'p-2 border-top-0',
                        centered: true,
                    }
                ) */

                // this.$emit('success', data)

                /*   this.model = {
                    order_type_id: null,
                    customer_id: null,
                    driver_id: null,

                    order_template_id: null,
                    date: scheduledDate,
                    time: scheduledTime,
                    signature: false,
                    assets: [],
                    // lat: 18.003713067980094,
                    //  long: -76.7883610725403,
                }
                this.$refs.customerForm.reset()
                this.actionType = 'view'
                this.displayOrder(data.id) */
            } catch (err) {
                console.log(err)
                this.doToast('Invalid', err.message, 'danger')
            } finally {
                this.submitting = false
            }
        },
    },
}
</script>
<style></style>
